import { getSessionSubstitutionRequestSummary, normalizeOriginalTeacherIssueReason } from '@airreading/common';
import {
  AdminSession,
  OriginalTeacherIssueSelectable,
  SessionStatus,
  SubstitutionRequestStatus,
  UpdateSessionTeacherOptions,
} from '@airreading/common/data';
import { shouldShowReasonField } from '@/lib/original-teacher-issue-utils.js';
import { nullIfString } from '@/lib/string-utils.js';

export type SessionTeacherAssignmentState = 'original_teacher' | 'confirmed_substitute' | 'pending_assignment';

export function getSessionTeacherAssignmentState(session: AdminSession): SessionTeacherAssignmentState {
  const { mostRecentRequest, coachIdWhoIsTeaching } = getSessionSubstitutionRequestSummary(session);

  if (mostRecentRequest?.status === SubstitutionRequestStatus.pending) {
    return 'pending_assignment';
  }

  if (coachIdWhoIsTeaching != null && coachIdWhoIsTeaching !== session.coachId) {
    return 'confirmed_substitute';
  }

  return 'original_teacher';
}

/** Matches server `updateSessionTeacher`: archived and cancelled sessions cannot change teacher. */
export function canEditSessionTeacher(session: AdminSession): boolean {
  if (session.status === SessionStatus.archived || session.status === SessionStatus.cancelled) {
    return false;
  }

  const assignmentState = getSessionTeacherAssignmentState(session);
  const { coachIdWhoIsTeaching } = getSessionSubstitutionRequestSummary(session);

  switch (assignmentState) {
    case 'pending_assignment':
      return true;
    case 'original_teacher':
      return session.coachId != null;
    case 'confirmed_substitute':
      return coachIdWhoIsTeaching != null;
  }
}

export type UpdateSessionTeacherAssignmentPayload = {
  sessionTeacherId: string;
  shouldSendConfirmationEmail?: boolean;
  originalTeacherClassIssue: OriginalTeacherIssueSelectable;
  reason?: string;
};

function buildOriginalTeacherIssuePayload(
  payload: UpdateSessionTeacherAssignmentPayload,
): Pick<UpdateSessionTeacherOptions, 'originalTeacherIssue' | 'originalTeacherIssueReason'> {
  return {
    originalTeacherIssue: nullIfString(payload.originalTeacherClassIssue),
    originalTeacherIssueReason: shouldShowReasonField(payload.originalTeacherClassIssue)
      ? normalizeOriginalTeacherIssueReason(payload.reason)
      : null,
  };
}

export function buildUpdateSessionTeacherPayload(
  session: AdminSession,
  payload: UpdateSessionTeacherAssignmentPayload,
): UpdateSessionTeacherOptions {
  const newSessionCoachId = payload.sessionTeacherId;
  const sendConfirmationEmail = payload.shouldSendConfirmationEmail ?? false;
  const assignmentState = getSessionTeacherAssignmentState(session);
  const { coachIdWhoIsTeaching } = getSessionSubstitutionRequestSummary(session);

  if (assignmentState === 'pending_assignment') {
    return {
      action: 'fulfill',
      newSessionCoachId,
      sendConfirmationEmail,
    };
  }

  const requestedByCoachId = assignmentState === 'original_teacher' ? session.coachId : coachIdWhoIsTeaching;

  if (!requestedByCoachId) {
    throw new Error('Cannot determine which teacher is stepping out for this session');
  }

  return {
    action: 'sub_and_fulfill',
    newSessionCoachId,
    requestedByCoachId,
    sendConfirmationEmail,
    ...(assignmentState === 'original_teacher' ? buildOriginalTeacherIssuePayload(payload) : {}),
  };
}
